import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLogin } from '../../../hooks/useLogin';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { resetPassword } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('Invalid or missing token.');
      return;
    }
    if (!password || !confirm) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (/\s/.test(password)) {
      setError('Password cannot contain spaces.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await resetPassword(token, password);
      if (result.success) {
        setSuccess('Password reset successful! Redirecting to login...');
        setTimeout(() => navigate('/LoginRegister'), 2000);
      } else {
        setError(result.error || result.message || 'Failed to reset password.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  // Password validation helpers (same as registration)
  const hasMinLength = (pwd: string) => pwd.length >= 8;
  const hasNumber = (pwd: string) => /\d/.test(pwd);
  const hasSpecialChar = (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
  const passwordsMatch = () => password && password === confirm;

  return (
    <div className={styles.container}>
      <div className={styles.formSection}>
      <h2>Reset Password</h2>
      {error && <p className={styles.errorMessage}>{error}</p>}
      {success && <p className={styles.successMessage}>{success}</p>}
      <form onSubmit={handleSubmit} className={styles.form}>
        <label htmlFor="password">New Password:</label>
  <div className={styles.inputRow}>
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value.replace(/\s/g, ''))}
            required
            disabled={submitting}
            className={styles.inputField}
          />
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.link} ${styles.showHideButton}`}
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {/* Password Requirements Indicator */}
        {password && (
          <div className={styles.passwordRequirements}>
            <div className={`${styles.requirementItem} ${hasMinLength(password) ? styles.met : styles.unmet}`}>
              <span className={styles.requirementIcon}>{hasMinLength(password) ? '✓' : '○'}</span>
              <span>At least 8 characters</span>
            </div>
            <div className={`${styles.requirementItem} ${hasNumber(password) ? styles.met : styles.unmet}`}>
              <span className={styles.requirementIcon}>{hasNumber(password) ? '✓' : '○'}</span>
              <span>Contains a number</span>
            </div>
            <div className={`${styles.requirementItem} ${hasSpecialChar(password) ? styles.met : styles.unmet}`}>
              <span className={styles.requirementIcon}>{hasSpecialChar(password) ? '✓' : '○'}</span>
              <span>Contains a special character (!@#$%^&* etc.)</span>
            </div>
          </div>
        )}
        <label htmlFor="confirm">Confirm Password:</label>
  <div className={styles.inputRow}>
          <input
            type={showConfirm ? 'text' : 'password'}
            id="confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\s/g, ''))}
            required
            disabled={submitting}
            className={styles.inputField}
          />
          <button
            type="button"
            className={`${buttonStyles.button} ${buttonStyles.link} ${styles.showHideButton}`}
            onClick={() => setShowConfirm(s => !s)}
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirm ? 'Hide' : 'Show'}
          </button>
        </div>
        {/* Password Match Indicator */}
        {confirm && (
          <div className={`${styles.passwordMatchIndicator} ${passwordsMatch() ? styles.match : styles.noMatch}`}>
            <span className={styles.requirementIcon}>{passwordsMatch() ? '✓' : '✗'}</span>
            <span>{passwordsMatch() ? 'Passwords match' : 'Passwords do not match'}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={
            submitting ||
            !hasMinLength(password) ||
            !hasNumber(password) ||
            !hasSpecialChar(password) ||
            !passwordsMatch()
          }
          className={`${buttonStyles.button} ${buttonStyles.form}`}
        >
          {submitting ? 'Resetting...' : 'Reset Password'}
        </button>

        <button
          type="button"
          className={`${buttonStyles.button} ${buttonStyles.link}`}
          onClick={() => navigate('/LoginRegister')}
        >
          Back to Login
        </button>
      </form>
      </div>
    </div>
  );
};

export default ResetPassword;
